/**
 * La máquina de estados de una guía en curso.
 *
 * Cada paso se prepara en tres tiempos y en este orden, porque saltárselo es
 * lo que hace que las guías señalen al vacío:
 *
 *   1. **Navegar**, si el paso vive en otra pantalla — y sólo cuando no hay
 *      un modal abierto, porque navegar con un formulario a medias tira lo
 *      que el operador llevaba escrito. En esta pantalla eso puede ser una
 *      respuesta a medio redactar.
 *   2. **Esperar al objetivo**, que puede tardar dos renders y una petición
 *      en existir (`waitForTarget`).
 *   3. **Mostrar**, con el elemento ya medido.
 *
 * Si el objetivo no llega: el paso marcado `optionalTarget` se muestra
 * centrado —su texto se sostiene solo—; el resto se salta **en la dirección
 * en la que iba el operador**. Saltarlo siempre hacia adelante dejaba el
 * botón «Atrás» muerto: retrocedías a un paso que no se podía mostrar y él
 * te devolvía justo de donde venías.
 *
 * Lo que nunca pasa es quedarse esperando: el operador está trabajando.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { markCompleted } from './progress';
import { selectSteps } from './selectFlows';
import { waitForNoDialog, waitForTarget } from './waitForTarget';
import type { TourContext, TourFlow, TourStep } from './types';

/** `preparing` = navegando o esperando al objetivo. `active` = a la vista. */
export type TourStatus = 'idle' | 'preparing' | 'active';

export interface TourEngine {
  flow: TourFlow | null;
  step: TourStep | null;
  steps: TourStep[];
  index: number;
  status: TourStatus;
  /** El elemento resaltado. `null` en los pasos centrados. */
  targetEl: HTMLElement | null;
  isFirst: boolean;
  isLast: boolean;
  start: (flow: TourFlow) => void;
  next: () => void;
  prev: () => void;
  stop: () => void;
}

/** Lo que se espera a un objetivo antes de darlo por ausente. */
export const TARGET_TIMEOUT_MS = 6000;

/**
 * Un objetivo opcional recibe **una quinta parte** de esa paciencia.
 *
 * La espera larga existe para las pantallas que tardan en pintar: se navega,
 * se descarga el trozo de código y encima se pide el dato. Pero un paso
 * marcado `optionalTarget` tiene texto que se sostiene solo, así que
 * esperarle seis segundos sólo consigue que la guía se quede invisible ese
 * rato — y si el operador cambia de pantalla mientras tanto, el paso aparece
 * encima de otra vista.
 *
 * Es lo que pasaba con el aviso de alertas de Monitoreo: el ancla existe
 * siempre, pero mide cero cuando no hay ninguna alerta que enseñar.
 */
export const FACTOR_OBJETIVO_OPCIONAL = 5;

interface UseTourEngineArgs {
  ctx: TourContext;
  /** Clave del progreso; el motor la usa para marcar inicio y final. */
  progressKey: string;
  onCompleted?: (flowId: string) => void;
  /** Sólo para las pruebas: acorta la espera del objetivo. */
  targetTimeoutMs?: number;
  /** Sólo para las pruebas: acorta la espera a que se cierre un modal. */
  dialogTimeoutMs?: number;
}

export function useTourEngine({
  ctx,
  progressKey,
  onCompleted,
  targetTimeoutMs = TARGET_TIMEOUT_MS,
  dialogTimeoutMs,
}: UseTourEngineArgs): TourEngine {
  const navigate = useNavigate();
  const location = useLocation();

  const [flow, setFlow] = useState<TourFlow | null>(null);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [index, setIndex] = useState(0);

  /**
   * El resultado de la preparación, **etiquetado con el paso que lo
   * produjo**. Guardarlo así en vez de en dos estados sueltos es lo que
   * permite que al cambiar de paso el resultado viejo deje de valer en el
   * mismo render: no hay que limpiarlo desde el efecto, y no se ve un
   * fotograma con el recorte del paso anterior.
   */
  const [prepared, setPrepared] = useState<{
    stepKey: string;
    targetEl: HTMLElement | null;
  } | null>(null);

  /**
   * Hacia dónde iba el operador. Sólo la usa el salto del paso que no se
   * puede mostrar, y por eso es una `ref`: cambiarla no tiene que repintar
   * nada ni reiniciar la preparación en curso.
   */
  const direccion = useRef<'adelante' | 'atras'>('adelante');

  // El contexto y la ruta cambian con cada render del layout; el efecto de
  // preparación depende sólo del paso. Por referencia, para no reiniciar la
  // preparación cada vez que el padre se vuelve a pintar.
  const ctxRef = useRef(ctx);
  const pathnameRef = useRef(location.pathname);
  const indexRef = useRef(0);

  useEffect(() => {
    ctxRef.current = ctx;
    pathnameRef.current = location.pathname;
  }, [ctx, location.pathname]);

  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const stop = useCallback(() => {
    setFlow(null);
    setSteps([]);
    setIndex(0);
    setPrepared(null);
  }, []);

  const start = useCallback(
    (nextFlow: TourFlow) => {
      const visible = selectSteps(nextFlow, ctxRef.current);
      if (visible.length === 0) return;

      setFlow(nextFlow);
      setSteps(visible);
      setIndex(0);
      setPrepared(null);
      direccion.current = 'adelante';
    },
    [progressKey],
  );

  const finish = useCallback(() => {
    if (flow) {
      markCompleted(progressKey, flow.id);
      onCompleted?.(flow.id);
    }
    stop();
  }, [flow, onCompleted, progressKey, stop]);

  const next = useCallback(() => {
    direccion.current = 'adelante';
    setIndex((current) => {
      if (current >= steps.length - 1) {
        // El final se resuelve fuera del setter para no encadenar dos
        // actualizaciones de estado dentro de la misma.
        queueMicrotask(finish);
        return current;
      }
      return current + 1;
    });
  }, [finish, steps.length]);

  const prev = useCallback(() => {
    direccion.current = 'atras';
    setIndex((current) => Math.max(0, current - 1));
  }, []);

  // ── Preparación del paso ────────────────────────────────────────────
  const step: TourStep | null = flow ? (steps[index] ?? null) : null;
  const stepKey = step ? `${flow?.id}:${step.id}` : null;

  // Derivado, no almacenado: mientras el resultado guardado no sea el de
  // este paso, el paso está preparándose.
  const isPrepared = prepared !== null && prepared.stepKey === stepKey;
  const status: TourStatus = !flow ? 'idle' : isPrepared ? 'active' : 'preparing';
  const targetEl = isPrepared ? prepared.targetEl : null;

  useEffect(() => {
    if (!flow || !step || !stepKey) return;

    const controller = new AbortController();
    const { signal } = controller;
    let cancelled = false;

    const prepare = async () => {
      if (step.route && step.route !== pathnameRef.current) {
        // Con un modal abierto se espera a que el operador lo cierre. Si no
        // lo cierra, el paso se queda preparando y él sigue trabajando: la
        // guía es un acompañante, no un secuestro.
        const clear = await waitForNoDialog({ signal, timeoutMs: dialogTimeoutMs });
        if (cancelled) return;
        if (!clear) {
          // Se agotó la espera con el modal todavía abierto. Antes se salía
          // sin más y la guía quedaba en un limbo: ni tarjeta, ni velo, y el
          // botón de la esquina escondido porque para el contexto seguía
          // «en curso». Sólo se salía con Escape, que nadie sabe que hay que
          // pulsar. Se cierra, y el botón vuelve para retomarla.
          stop();
          return;
        }
        navigate(step.route);
      }

      if (!step.target) {
        if (!cancelled) setPrepared({ stepKey, targetEl: null });
        return;
      }

      const espera = step.optionalTarget
        ? Math.round(targetTimeoutMs / FACTOR_OBJETIVO_OPCIONAL)
        : targetTimeoutMs;
      const el = await waitForTarget(step.target, { signal, timeoutMs: espera });
      if (cancelled) return;

      if (!el) {
        if (step.optionalTarget) {
          // Sin elemento pero con texto que se sostiene: paso centrado.
          setPrepared({ stepKey, targetEl: null });
        } else if (direccion.current === 'atras' && indexRef.current > 0) {
          // Se salta hacia atrás, que es hacia donde iba. En el primer paso
          // no hay a dónde retroceder, así que se sigue hacia adelante: es
          // preferible avanzar a dejar la guía sin nada que enseñar.
          prev();
        } else {
          // Ni elemento ni sentido sin él. Se salta hacia adelante; si era el
          // último, la guía termina.
          direccion.current = 'adelante';
          next();
        }
        return;
      }

      el.scrollIntoView({ block: 'center', inline: 'nearest' });
      setPrepared({ stepKey, targetEl: el });
    };

    void prepare();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `next` y `prev` cambian con `steps.length`, y meterlos aquí
    // reiniciaría la preparación a mitad. El paso es la única entrada real.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepKey]);

  // ── Avanzar pulsando el elemento real ───────────────────────────────
  // `advanceOn: 'click-target'` es lo que permite que la guía acompañe una
  // acción sin ejecutarla. El motor jamás dispara el clic: escucha.
  useEffect(() => {
    if (!targetEl || step?.advanceOn !== 'click-target') return;

    const onClick = () => next();
    targetEl.addEventListener('click', onClick);
    return () => targetEl.removeEventListener('click', onClick);
  }, [targetEl, step?.advanceOn, next]);

  // ── Salirse de la pantalla que la guía explica ──────────────────────
  //
  // Una guía habla de una pantalla concreta. Si el operador se va a otra, sus
  // pasos siguientes salen encima de algo que no tienen nada que ver — el
  // paso 4 de Monitoreo estando en Clientes. Pasaba sobre todo mientras un
  // paso se preparaba: la guía estaba invisible, el operador cambiaba de
  // pestaña, y reaparecía allí.
  //
  // Se cierra en cuanto sale, y no antes: la propia guía navega a su pantalla
  // en el primer paso, así que hasta haber estado dentro una vez no se aplica.
  const haEstadoDentro = useRef(false);

  useEffect(() => {
    if (!flow?.href) return;

    const dentro =
      location.pathname === flow.href ||
      location.pathname.startsWith(`${flow.href}/`);

    if (dentro) {
      haEstadoDentro.current = true;
      return;
    }
    if (haEstadoDentro.current) stop();
  }, [flow?.href, location.pathname, stop]);

  useEffect(() => {
    if (!flow) haEstadoDentro.current = false;
  }, [flow]);

  // ── Salir con Escape ────────────────────────────────────────────────
  useEffect(() => {
    if (!flow) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stop();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [flow, stop]);

  return {
    flow,
    step,
    steps,
    index,
    status,
    targetEl,
    isFirst: index === 0,
    isLast: index === steps.length - 1,
    start,
    next,
    prev,
    stop,
  };
}
